import { useState } from "react"
import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { Facebook, Linkedin, Twitter } from "lucide-react"
import { supabase } from "@/lib/supabase"

const footerCategories = {
  product: [
    { label: "Sign up", href: "/signup" },
    { label: "Pricing", href: "/pricing" },
    { label: "Features", href: "/#how" },
    { label: "Health Encyclopedia", href: "/health" },
  ],
  legal: [
    { label: "Terms", href: "/terms" },
    { label: "Privacy", href: "/privacy" },
    { label: "Cookies", href: "/cookies" },
    { label: "Accessibility", href: "/accessibility" },
  ],
  contact: [
    { label: "Support", href: "mailto:support@diagnoza.care" },
    { label: "Partnerships", href: "mailto:partners@diagnoza.care" },
  ],
}

const socialLinks = [
  { icon: Twitter, href: "https://twitter.com/diagnoza", label: "Twitter" },
  { icon: Facebook, href: "https://www.facebook.com/profile.php?id=61563762070947", label: "Facebook" },
  { icon: Linkedin, href: "https://linkedin.com/company/diagnoza", label: "LinkedIn" },
]

type SiteFooterProps = {
  className?: string
}

export function SiteFooter({ className }: SiteFooterProps) {
  const [email, setEmail] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [subscribeSuccess, setSubscribeSuccess] = useState(false)
  const [subscribeError, setSubscribeError] = useState("")

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setSubscribeError("")
    setSubscribeSuccess(false)

    try {
      const { error } = await supabase
        .from("newsletter_subscribers")
        .insert([{ email, source: "footer" }])

      if (error) {
        // Check if it's a duplicate email error
        if (error.code === "23505") {
          setSubscribeError("You're already subscribed!")
        } else {
          setSubscribeError(error.message)
        }
        setIsLoading(false)
        return
      }

      setSubscribeSuccess(true)
      setEmail("")
      setIsLoading(false)

      // Hide success message after 5 seconds
      setTimeout(() => setSubscribeSuccess(false), 5000)
    } catch (err) {
      console.error("Newsletter subscription error:", err)
      setSubscribeError("An error occurred. Please try again.")
      setIsLoading(false)
    }
  }

  return (
    <footer className={cn("border-t border-slate-200 bg-slate-50", className)}>
      <div className="mx-auto max-w-7xl px-6 py-12">
        <div className="grid gap-8 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <a className="mb-4 flex items-center gap-3" href="/">
              <img
                src="/web-app-manifest-192x192.png"
                alt="Diagnoza"
                className="h-11 w-11 object-contain"
              />
              <div>
                <p className="text-[11px] uppercase tracking-[0.4em] text-muted-foreground">Diagnoza</p>
                <p className="text-lg font-semibold leading-none">care</p>
              </div>
            </a>
            <p className="mb-4 max-w-md text-sm text-muted-foreground">
              Personalized AI health companion that remembers every detail for health anxiety and chronic conditions.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => {
                const Icon = social.icon
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    aria-label={social.label}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200/70 bg-white text-muted-foreground transition hover:border-primary hover:text-primary"
                  >
                    <Icon className="size-4" />
                  </a>
                )
              })}
            </div>
          </div>

          <div>
            <h3 className="mb-4 font-semibold text-slate-900">Product</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              {footerCategories.product.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="hover:text-slate-900">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 font-semibold text-slate-900">Legal</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              {footerCategories.legal.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="hover:text-slate-900">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 font-semibold text-slate-900">Contact</h3>
            <ul className="mb-6 space-y-3 text-sm text-muted-foreground">
              {footerCategories.contact.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="hover:text-slate-900">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
            <h4 className="mb-3 text-sm font-semibold text-slate-900">Newsletter</h4>
            <p className="mb-3 text-sm text-muted-foreground">Get updates and health tips</p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <Input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-slate-200 bg-white"
                required
              />

              {/* Success message */}
              {subscribeSuccess && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-2"
                >
                  <p className="text-xs text-emerald-700">
                    <strong>Success!</strong> You're subscribed.
                  </p>
                </motion.div>
              )}

              {/* Error message */}
              {subscribeError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-lg border border-red-100 bg-red-50/50 p-2"
                >
                  <p className="text-xs text-red-700">
                    <strong>Error:</strong> {subscribeError}
                  </p>
                </motion.div>
              )}

              <Button type="submit" disabled={isLoading} className="w-full" size="sm">
                {isLoading ? "Subscribing..." : "Subscribe"}
              </Button>
            </form>
          </div>
        </div>

        <div className="mt-12 border-t border-slate-200 pt-8 text-center text-sm text-muted-foreground">
          © {new Date().getFullYear()} Diagnoza.care. All rights reserved.
        </div>
      </div>
    </footer>
  )
}
